import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface BorderCrossing {
  id: string;
  name: string;
  display_name: string;
  location: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  lane_types: string[];
  modes: string[];
  is_active: boolean;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export const useBorderCrossings = () => {
  const [crossings, setCrossings] = useState<BorderCrossing[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchCrossings = async () => {
    try {
      const { data, error } = await supabase
        .from('border_crossings')
        .select('*')
        .eq('is_active', true)
        .order('order_index');

      if (error) throw error;

      const transformedData: BorderCrossing[] = (data || []).map(item => ({
        ...item,
        coordinates: item.coordinates as any
      }));
      setCrossings(transformedData);
    } catch (error) {
      console.error('Error fetching border crossings:', error);
      toast({
        title: "Error",
        description: "Failed to load border crossings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCrossings();
  }, []);

  const getCrossingByName = (name: string) => {
    return crossings.find(crossing => crossing.name === name);
  };

  const getLaneTypesForCrossing = (crossingName: string) => {
    const crossing = getCrossingByName(crossingName);
    return crossing?.lane_types || [];
  };

  const getModesForCrossing = (crossingName: string) => {
    const crossing = getCrossingByName(crossingName);
    return crossing?.modes || [];
  };

  return {
    crossings,
    loading,
    refetch: fetchCrossings,
    getCrossingByName,
    getLaneTypesForCrossing,
    getModesForCrossing,
  };
};